import { Checkbox as CheckboxPrimitive } from '@base-ui/react/checkbox';
import { Tick02Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/** The bare box, for a table's select column. Label it with aria-label. */
function CheckboxBox({ className, ...props }: CheckboxPrimitive.Root.Props) {
  return (
    <CheckboxPrimitive.Root
      data-slot="checkbox"
      className={cn(
        'peer relative flex size-4 shrink-0 items-center justify-center rounded-sm border border-input bg-card transition-shadow after:absolute after:-inset-x-3 after:-inset-y-2 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive data-checked:border-primary data-checked:bg-primary data-checked:text-primary-foreground data-indeterminate:border-primary data-indeterminate:bg-primary data-indeterminate:text-primary-foreground',
        className,
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator
        data-slot="checkbox-indicator"
        className="grid place-content-center text-current transition-none [&>svg]:size-3.5"
      >
        <HugeiconsIcon icon={Tick02Icon} strokeWidth={2} />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
}

interface CheckboxProps {
  name?: string;
  checked?: boolean | 'indeterminate';
  defaultChecked?: boolean;
  disabled?: boolean;
  className?: string;
  onCheckedChange?: (checked: boolean) => void;
  /** The label. It is part of the control: pressing it toggles the box. */
  children: ReactNode;
}

/** A labelled checkbox with the portal's older prop names. */
function Checkbox({
  checked,
  defaultChecked,
  disabled,
  onCheckedChange,
  className,
  children,
  name,
}: Readonly<CheckboxProps>) {
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: the label wraps the Base UI checkbox, which renders its own input.
    <label
      className={cn(
        'flex min-h-8 items-center gap-2 text-sm',
        disabled && 'opacity-50',
        className,
      )}
    >
      <CheckboxBox
        name={name}
        checked={checked === undefined ? undefined : checked === true}
        indeterminate={checked === 'indeterminate'}
        defaultChecked={defaultChecked}
        disabled={disabled}
        onCheckedChange={(next) => onCheckedChange?.(next)}
      />
      <span>{children}</span>
    </label>
  );
}

export { Checkbox, CheckboxBox };
